// src/utils/chatDebug.ts
// Centralized debug logging for ChatHub with localStorage toggle

const STORAGE_KEY = 'AX1_CHAT_DEBUG';

// ─── Check if chat debug is enabled ──────────────────────────────────────────
/**
 * Returns true if CHAT_DEBUG is enabled.
 * - Production: always false unless explicitly enabled via localStorage
 * - Dev: reads from localStorage key "AX1_CHAT_DEBUG", default false
 */
export function isChatDebugEnabled(): boolean {
  // Only allow in development or if explicitly enabled
  if (typeof window === 'undefined') return false;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
}

// ─── Toggle chat debug ───────────────────────────────────────────────────────
/**
 * Enable or disable chat debug logging.
 */
export function setChatDebugEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    // localStorage not available
  }
}

// ─── Throttle helper for high-frequency events ───────────────────────────────
const throttleMap = new Map<string, number>();
const THROTTLE_MS = 1000; // 1 second default throttle

function shouldThrottle(key: string, throttleMs = THROTTLE_MS): boolean {
  const now = Date.now();
  const lastLog = throttleMap.get(key) || 0;
  
  if (now - lastLog < throttleMs) {
    return true;
  }
  
  throttleMap.set(key, now);
  return false;
}

// ─── Debug logger ────────────────────────────────────────────────────────────
/**
 * Log a debug message if CHAT_DEBUG is enabled.
 * 
 * @param scope - The scope/area of the log (e.g., 'Presence', 'Typing', 'Messages')
 * @param message - The log message
 * @param data - Optional data object to include
 * @param options - Optional config: { throttle?: boolean, throttleMs?: number, throttleKey?: string }
 */
export function chatDebugLog(
  scope: string,
  message: string,
  data?: Record<string, unknown>,
  options?: { throttle?: boolean; throttleMs?: number; throttleKey?: string }
): void {
  if (!isChatDebugEnabled()) return;
  
  // Check throttling if requested
  if (options?.throttle) {
    const throttleKey = options.throttleKey || `${scope}:${message}`;
    if (shouldThrottle(throttleKey, options.throttleMs)) {
      return;
    }
  }
  
  const timestamp = new Date().toISOString().slice(11, 23); // HH:mm:ss.sss
  const prefix = `[ChatDebug][${scope}]`;
  
  if (data && Object.keys(data).length > 0) {
    console.log(`${timestamp} ${prefix} ${message}`, data);
  } else {
    console.log(`${timestamp} ${prefix} ${message}`);
  }
}

// ─── Scoped loggers for common areas ─────────────────────────────────────────
export const chatDebug = {
  presence: (message: string, data?: Record<string, unknown>, throttle = false) => 
    chatDebugLog('Presence', message, data, { throttle }),
  
  typing: (message: string, data?: Record<string, unknown>, throttle = false) => 
    chatDebugLog('Typing', message, data, { throttle }),
  
  messages: (message: string, data?: Record<string, unknown>, throttle = false) => 
    chatDebugLog('Messages', message, data, { throttle }),
  
  reactions: (message: string, data?: Record<string, unknown>, throttle = false) => 
    chatDebugLog('Reactions', message, data, { throttle }),
  
  channel: (message: string, data?: Record<string, unknown>) => 
    chatDebugLog('Channel', message, data),
  
  network: (message: string, data?: Record<string, unknown>) => 
    chatDebugLog('Network', message, data),
};

export default chatDebug;
